(() => {
  if (window.FcpCardSlideshowRuntime) {
    window.FcpCardSlideshowRuntime.init();
    return;
  }

  class FcpCardSlideshow {
    constructor(section) {
      this.section = section;
      this.viewport = section.querySelector('[data-fcp-slider-viewport]');
      this.track = section.querySelector('[data-fcp-slider-track]');
      this.prevButton = section.querySelector('[data-fcp-slider-prev]');
      this.nextButton = section.querySelector('[data-fcp-slider-next]');

      if (!this.viewport || !this.track) return;

      this.removeExistingClones();

      this.originalSlides = Array.from(this.track.querySelectorAll('[data-fcp-slider-slide]'));
      this.originalCount = this.originalSlides.length;

      if (this.originalCount === 0) return;

      this.loop = section.dataset.loop === 'true' && this.originalCount > 1;
      this.autoplay = section.dataset.autoplay === 'true' && this.originalCount > 1;
      this.autoplayDelay = Number(section.dataset.autoplayDelay) || 5000;
      this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.currentIndex = 0;
      this.renderIndex = 0;
      this.cloneOffset = 0;

      this.currentTranslate = 0;
      this.slideWidth = 0;
      this.viewportWidth = 0;

      this.isDragging = false;
      this.isAnimating = false;
      this.hasDragged = false;
      this.suppressClick = false;

      this.dragStartX = 0;
      this.dragDeltaX = 0;
      this.pointerId = null;

      this.autoplayTimer = null;
      this.resizeTimer = null;
      this.animationTimer = null;
      this.resizeObserver = null;

      this.handlePrev = this.prev.bind(this);
      this.handleNext = this.next.bind(this);
      this.handlePointerDown = this.onPointerDown.bind(this);
      this.handlePointerMove = this.onPointerMove.bind(this);
      this.handlePointerUp = this.onPointerUp.bind(this);
      this.handleClickCapture = this.onClickCapture.bind(this);
      this.handleTransitionEnd = this.onTransitionEnd.bind(this);
      this.handleResize = this.onResize.bind(this);
      this.handleVisibilityChange = this.onVisibilityChange.bind(this);
      this.handleMouseEnter = this.stopAutoplay.bind(this);
      this.handleMouseLeave = this.startAutoplay.bind(this);
      this.handleFocusIn = this.stopAutoplay.bind(this);
      this.handleFocusOut = this.startAutoplay.bind(this);

      this.init();
    }

    init() {
      if (this.loop) {
        this.createClones();
      }

      this.slides = Array.from(this.track.querySelectorAll('[data-fcp-slider-slide]'));
      this.cloneOffset = this.loop ? this.originalCount : 0;
      this.renderIndex = this.cloneOffset;

      this.measure();
      this.goToRenderIndex(this.renderIndex, false);
      this.updateState();

      this.bindEvents();
      this.startAutoplay();
    }

    removeExistingClones() {
      this.track.querySelectorAll('[data-fcp-clone]').forEach((clone) => {
        clone.remove();
      });
    }

    createClones() {
      const beforeFragment = document.createDocumentFragment();
      const afterFragment = document.createDocumentFragment();

      this.originalSlides.forEach((slide) => {
        const clone = slide.cloneNode(true);
        this.prepareClone(clone, 'before');
        beforeFragment.appendChild(clone);
      });

      this.originalSlides.forEach((slide) => {
        const clone = slide.cloneNode(true);
        this.prepareClone(clone, 'after');
        afterFragment.appendChild(clone);
      });

      this.track.insertBefore(beforeFragment, this.track.firstChild);
      this.track.appendChild(afterFragment);
    }

    prepareClone(clone, position) {
      clone.setAttribute('aria-hidden', 'true');
      clone.setAttribute('data-fcp-clone', position);
      clone.dataset.fcpClone = position;

      clone.removeAttribute('data-shopify-editor-block');

      clone.querySelectorAll('[data-shopify-editor-block]').forEach((element) => {
        element.removeAttribute('data-shopify-editor-block');
      });

      clone.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach((element) => {
        element.setAttribute('tabindex', '-1');
      });
    }

    bindEvents() {
      if (this.prevButton) {
        this.prevButton.addEventListener('click', this.handlePrev);
      }

      if (this.nextButton) {
        this.nextButton.addEventListener('click', this.handleNext);
      }

      this.viewport.addEventListener('pointerdown', this.handlePointerDown);
      this.viewport.addEventListener('pointermove', this.handlePointerMove);
      this.viewport.addEventListener('pointerup', this.handlePointerUp);
      this.viewport.addEventListener('pointercancel', this.handlePointerUp);
      this.viewport.addEventListener('mouseleave', this.handlePointerUp);
      this.viewport.addEventListener('click', this.handleClickCapture, true);

      this.track.addEventListener('transitionend', this.handleTransitionEnd);
      window.addEventListener('resize', this.handleResize);
      document.addEventListener('visibilitychange', this.handleVisibilityChange);

      this.section.addEventListener('mouseenter', this.handleMouseEnter);
      this.section.addEventListener('mouseleave', this.handleMouseLeave);
      this.section.addEventListener('focusin', this.handleFocusIn);
      this.section.addEventListener('focusout', this.handleFocusOut);

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.onResize());
        this.resizeObserver.observe(this.viewport);
      }
    }

    measure() {
      this.viewportWidth = this.viewport.clientWidth;

      const sectionStyles = window.getComputedStyle(this.section);
      const trackStyles = window.getComputedStyle(this.track);

      const gap =
        parseFloat(trackStyles.getPropertyValue('--fcp-slide-gap')) ||
        parseFloat(trackStyles.columnGap) ||
        parseFloat(trackStyles.gap) ||
        0;

      const desktopCards =
        parseInt(sectionStyles.getPropertyValue('--fcp-slides-desktop'), 10) || 3;

      const tabletCards =
        parseInt(sectionStyles.getPropertyValue('--fcp-slides-tablet'), 10) || 2;

      const mobileRatio =
        parseFloat(sectionStyles.getPropertyValue('--fcp-mobile-card-ratio')) || 0.86;

      let visibleCards = 1;
      let slideWidth = this.viewportWidth * mobileRatio;

      if (window.matchMedia('(min-width: 990px)').matches) {
        visibleCards = desktopCards;
        slideWidth = (this.viewportWidth - gap * (visibleCards - 1)) / visibleCards;
      } else if (window.matchMedia('(min-width: 750px)').matches) {
        visibleCards = tabletCards;
        slideWidth = (this.viewportWidth - gap * (visibleCards - 1)) / visibleCards;
      }

      slideWidth = Math.max(220, Math.floor(slideWidth));

      this.viewport.style.setProperty('--fcp-slide-width-px', `${slideWidth}px`);

      const activeSlide = this.slides[this.renderIndex] || this.slides[0];
      this.slideWidth = activeSlide ? activeSlide.offsetWidth : slideWidth;
    }

    getTranslateForIndex(index) {
      const slide = this.slides[index];

      if (!slide) return 0;

      const slideCenter = slide.offsetLeft + slide.offsetWidth / 2;
      const viewportCenter = this.viewport.clientWidth / 2;

      return viewportCenter - slideCenter;
    }

    setTranslate(value) {
      this.currentTranslate = value;
      this.track.style.transform = `translate3d(${value}px, 0, 0)`;
    }

    goToRenderIndex(index, animate = true) {
      if (!this.loop) {
        index = Math.max(0, Math.min(index, this.originalCount - 1));
      }

      this.renderIndex = index;

      const shouldAnimate = animate && !this.prefersReducedMotion;

      window.clearTimeout(this.animationTimer);

      if (shouldAnimate) {
        this.isAnimating = true;
        this.track.classList.add('is-animating');

        this.animationTimer = window.setTimeout(() => {
          this.finishAnimation();
        }, 700);
      } else {
        this.isAnimating = false;
        this.track.classList.remove('is-animating');
      }

      this.setTranslate(this.getTranslateForIndex(this.renderIndex));
      this.updateState();

      if (!shouldAnimate) {
        this.finishAnimation();
      }
    }

    getOriginalIndexFromRenderIndex(renderIndex) {
      if (!this.loop) {
        return Math.max(0, Math.min(renderIndex, this.originalCount - 1));
      }

      return ((renderIndex - this.cloneOffset) % this.originalCount + this.originalCount) % this.originalCount;
    }

    updateState() {
      const activeOriginalIndex = this.getOriginalIndexFromRenderIndex(this.renderIndex);
      this.currentIndex = activeOriginalIndex;

      this.slides.forEach((slide, index) => {
        const isClone = Boolean(slide.dataset.fcpClone);
        const isActive = index === this.renderIndex;
        const distance = Math.abs(index - this.renderIndex);
        const isNear = distance === 1;

        slide.classList.toggle('is-active', isActive);
        slide.classList.toggle('is-near', isNear);

        if (isClone) {
          slide.setAttribute('aria-hidden', 'true');
          slide.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach((element) => {
            element.setAttribute('tabindex', '-1');
          });
        } else {
          slide.setAttribute('aria-hidden', 'false');
          slide.querySelectorAll('a, button, input, select, textarea').forEach((element) => {
            element.removeAttribute('tabindex');
          });
        }
      });

      if (!this.loop) {
        if (this.prevButton) this.prevButton.disabled = this.renderIndex <= 0;
        if (this.nextButton) this.nextButton.disabled = this.renderIndex >= this.originalCount - 1;
      }
    }

    prev() {
      if (this.isAnimating || this.originalCount <= 1) return;

      this.stopAutoplay();
      this.goToRenderIndex(this.renderIndex - 1, true);
      this.startAutoplay();
    }

    next() {
      if (this.isAnimating || this.originalCount <= 1) return;

      this.stopAutoplay();
      this.goToRenderIndex(this.renderIndex + 1, true);
      this.startAutoplay();
    }

    onPointerDown(event) {
      if (event.button !== undefined && event.button !== 0) return;
      if (this.originalCount <= 1) return;

      this.stopAutoplay();

      this.isDragging = true;
      this.hasDragged = false;
      this.suppressClick = false;

      this.dragStartX = event.clientX;
      this.dragDeltaX = 0;
      this.pointerId = event.pointerId;

      this.viewport.classList.add('is-dragging');
      this.track.classList.remove('is-animating');

      if (this.viewport.setPointerCapture && this.pointerId !== null) {
        this.viewport.setPointerCapture(this.pointerId);
      }
    }

    onPointerMove(event) {
      if (!this.isDragging) return;

      this.dragDeltaX = event.clientX - this.dragStartX;

      if (Math.abs(this.dragDeltaX) > 8) {
        this.hasDragged = true;
      }

      const isDraggingPastStart = this.renderIndex <= 0 && this.dragDeltaX > 0;
      const isDraggingPastEnd = this.renderIndex >= this.originalCount - 1 && this.dragDeltaX < 0;
      const resistance = !this.loop && (isDraggingPastStart || isDraggingPastEnd) ? 0.35 : 1;

      this.setTranslate(this.getTranslateForIndex(this.renderIndex) + this.dragDeltaX * resistance);
    }

    onPointerUp() {
      if (!this.isDragging) return;

      const threshold = Math.max(this.slideWidth * 0.18, 48);
      const delta = this.dragDeltaX;

      this.isDragging = false;
      this.dragDeltaX = 0;
      this.viewport.classList.remove('is-dragging');

      if (this.hasDragged) {
        this.suppressClick = true;

        window.setTimeout(() => {
          this.suppressClick = false;
        }, 0);
      }

      if (this.viewport.releasePointerCapture && this.pointerId !== null) {
        try {
          this.viewport.releasePointerCapture(this.pointerId);
        } catch (error) {
          // Browser may already have released pointer capture.
        }
      }

      this.pointerId = null;

      if (delta < -threshold) {
        this.goToRenderIndex(this.renderIndex + 1, true);
      } else if (delta > threshold) {
        this.goToRenderIndex(this.renderIndex - 1, true);
      } else {
        this.goToRenderIndex(this.renderIndex, true);
      }

      this.startAutoplay();
    }

    onClickCapture(event) {
      if (!this.suppressClick) return;

      event.preventDefault();
      event.stopPropagation();
      this.suppressClick = false;
    }

    onTransitionEnd(event) {
      if (event.target !== this.track) return;
      if (event.propertyName && event.propertyName !== 'transform') return;

      this.finishAnimation();
    }

    finishAnimation() {
      window.clearTimeout(this.animationTimer);

      this.isAnimating = false;
      this.track.classList.remove('is-animating');

      if (!this.loop) {
        this.updateState();
        return;
      }

      if (this.renderIndex >= this.cloneOffset + this.originalCount) {
        this.renderIndex = this.cloneOffset;
        this.setTranslate(this.getTranslateForIndex(this.renderIndex));
      } else if (this.renderIndex < this.cloneOffset) {
        this.renderIndex = this.cloneOffset + this.originalCount - 1;
        this.setTranslate(this.getTranslateForIndex(this.renderIndex));
      }

      this.updateState();
    }

    onResize() {
      window.clearTimeout(this.resizeTimer);

      this.resizeTimer = window.setTimeout(() => {
        this.measure();

        if (this.loop) {
          this.renderIndex = this.cloneOffset + this.currentIndex;
        } else {
          this.renderIndex = Math.min(this.renderIndex, this.originalCount - 1);
        }

        this.goToRenderIndex(this.renderIndex, false);
      }, 120);
    }

    onVisibilityChange() {
      if (document.hidden) {
        this.stopAutoplay();
      } else {
        this.startAutoplay();
      }
    }

    startAutoplay() {
      this.stopAutoplay();

      if (!this.autoplay || this.originalCount <= 1 || document.hidden) return;

      this.autoplayTimer = window.setInterval(() => {
        this.next();
      }, this.autoplayDelay);
    }

    stopAutoplay() {
      if (this.autoplayTimer) {
        window.clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      }
    }

    destroy() {
      this.stopAutoplay();
      window.clearTimeout(this.resizeTimer);
      window.clearTimeout(this.animationTimer);

      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
      }

      if (this.prevButton) {
        this.prevButton.removeEventListener('click', this.handlePrev);
      }

      if (this.nextButton) {
        this.nextButton.removeEventListener('click', this.handleNext);
      }

      this.viewport.removeEventListener('pointerdown', this.handlePointerDown);
      this.viewport.removeEventListener('pointermove', this.handlePointerMove);
      this.viewport.removeEventListener('pointerup', this.handlePointerUp);
      this.viewport.removeEventListener('pointercancel', this.handlePointerUp);
      this.viewport.removeEventListener('mouseleave', this.handlePointerUp);
      this.viewport.removeEventListener('click', this.handleClickCapture, true);

      this.track.removeEventListener('transitionend', this.handleTransitionEnd);
      window.removeEventListener('resize', this.handleResize);
      document.removeEventListener('visibilitychange', this.handleVisibilityChange);

      this.section.removeEventListener('mouseenter', this.handleMouseEnter);
      this.section.removeEventListener('mouseleave', this.handleMouseLeave);
      this.section.removeEventListener('focusin', this.handleFocusIn);
      this.section.removeEventListener('focusout', this.handleFocusOut);

      this.removeExistingClones();
      this.track.style.transform = '';
      this.track.classList.remove('is-animating');
    }
  }

  const instances = new WeakMap();

  function init(container = document) {
    container.querySelectorAll('[data-fcp-card-slideshow]').forEach((section) => {
      if (instances.has(section)) return;

      const instance = new FcpCardSlideshow(section);
      instances.set(section, instance);
    });
  }

  function destroy(container = document) {
    container.querySelectorAll('[data-fcp-card-slideshow]').forEach((section) => {
      const instance = instances.get(section);

      if (!instance) return;

      instance.destroy();
      instances.delete(section);
    });
  }

  window.FcpCardSlideshowRuntime = {
    init,
    destroy
  };

  document.addEventListener('DOMContentLoaded', () => {
    init();
  });

  document.addEventListener('shopify:section:load', (event) => {
    init(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    destroy(event.target);
  });
})();